import { useCallback, useMemo, useRef } from "react";
import moment from "moment";
import {
   DefaultValues,
   FieldValues,
   UseFormHandleSubmit,
   useForm as useReactHookForm,
   UseFormReset,
   UseFormGetValues,
} from "react-hook-form";
import { MapMomentFieldValues, UseFormProps, UseFormReturn } from "./type";
import { deepMap, hasKeyInArray } from "./helpers";

const useForm = <
   TFieldValues extends FieldValues = FieldValues,
   TContext = unknown
>(
   props?: UseFormProps<TFieldValues, TContext>
): UseFormReturn<TFieldValues, TContext> => {
   const momentFields = useRef(props?.momentFields ?? []);

   const parseInputData = useCallback(<T = Partial<TFieldValues>>(data: T) => {
      const parseValue = (value: unknown, key: string) => {
         if (hasKeyInArray(momentFields.current, key) && moment.isMoment(value)) {
            return value.format();
         }
         return value;
      };
      return deepMap(parseValue, data as object) as MapMomentFieldValues<TFieldValues>;
   }, []);

   const parseOutputData = useCallback(
      <T = TFieldValues>(data: MapMomentFieldValues<T>): T => {
         const parseValue = (value: unknown, key: string) => {
            if (hasKeyInArray(momentFields.current, key) && value) {
              return moment(value)
            }
            return value;
         }
         return deepMap(parseValue, data) as T;
      },
      []
   );

   const defaultValues = useMemo(() => {
      return parseInputData(props?.defaultValues ?? {}) as DefaultValues<MapMomentFieldValues<TFieldValues>>;
   }, [parseInputData]);

   const hookFormReturn = useReactHookForm<MapMomentFieldValues<TFieldValues>, TContext>({
      ...props,
      defaultValues,
   });

   const handleSubmit: UseFormHandleSubmit<TFieldValues> = useCallback(
      (onValid, onInvalid) => async (e): Promise<void> => {
         hookFormReturn.handleSubmit((d, e) => {
            onValid(parseOutputData(d), e);
         }, onInvalid)(e);
      },
      [hookFormReturn.handleSubmit, parseOutputData]
   );

   const reset: UseFormReset<TFieldValues> = useCallback((values, keepStateOptions) => {
      const parsedResetValues = parseInputData(values) as MapMomentFieldValues<TFieldValues>;
      hookFormReturn.reset(parsedResetValues, keepStateOptions);
   }, [hookFormReturn.reset, parseInputData]);

   const getValues = useCallback((name) => {
      const data = hookFormReturn.getValues(name);
      return parseOutputData(data as TFieldValues);
   }, [hookFormReturn.getValues]) as UseFormGetValues<TFieldValues>

   return {
      ...hookFormReturn,
      reset,
      handleSubmit,
      getValues
   };
};

export default useForm;
